import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Database, FileText, Upload, Sparkles } from "lucide-react";
import { T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer, container, eyebrow, h2, italicAccent, btnCoral } from "@/lib/hybridTheme";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LIST_TYPES = [
  { v: "past_quotes",     l: "Past quotes / estimates" },
  { v: "cold_inquiries",  l: "Inquiries that went cold" },
  { v: "past_customers",  l: "Past customers" },
  { v: "mixed",           l: "Mixed" },
];
const LIST_AGES = [
  { v: "under_3mo",  l: "Under 3 months" },
  { v: "3_to_12mo",  l: "3–12 months" },
  { v: "1_to_3yr",   l: "1–3 years" },
  { v: "over_3yr",   l: "Older than 3 years" },
];
const CHANNELS = [
  { v: "sms",   l: "SMS" },
  { v: "voice", l: "Voice call" },
  { v: "both",  l: "Both" },
];

export default function ReactivationIntake() {
  const { token } = useParams();
  const [intake, setIntake] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [form, setForm] = useState({
    business_name: "", list_type: "", list_age: "",
    ai_reference_context: "", avg_deal_value: "",
    consent_confirmed: false, outreach_channels: [],
    booking_destination: "",
  });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/reactivation/public/intake/${token}`);
      setIntake(data);
      if (data.answers) {
        setForm({
          business_name:        data.answers.business_name || data.client_business_name || "",
          list_type:            data.answers.list_type || "",
          list_age:             data.answers.list_age || "",
          ai_reference_context: data.answers.ai_reference_context || "",
          avg_deal_value:       data.answers.avg_deal_value || "",
          consent_confirmed:    !!data.answers.consent_confirmed,
          outreach_channels:    data.answers.outreach_channels || [],
          booking_destination:  data.answers.booking_destination || "",
        });
      } else {
        setForm((f) => ({ ...f, business_name: data.client_business_name || "" }));
      }
      if (data.csv_uploaded) {
        setCsvResult({ filename: data.csv_filename, ...(data.csv_summary || {}) });
      }
    } catch { setNotFound(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (notFound) {
    return (
      <PageShell testId="reactivation-intake-404">
        <div style={{ padding: "120px 24px", textAlign: "center", color: T.ink }}>
          <div style={{ ...eyebrow(), color: T.coral }}>Link expired</div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: 48, margin: "16px 0" }}>Onboarding link not found.</h1>
          <p style={{ color: T.inkMuted, maxWidth: 480, margin: "0 auto" }}>Reach out to your Clients OnRamp contact for a fresh link.</p>
        </div>
      </PageShell>
    );
  }

  if (!intake) {
    return <PageShell><div style={{ padding: 48, color: T.inkMuted }}>Loading…</div></PageShell>;
  }

  const submitted = intake.status === "submitted";

  const toggleChannel = (v) => {
    setForm((f) => {
      const has = f.outreach_channels.includes(v);
      return { ...f, outreach_channels: has ? f.outreach_channels.filter((x) => x !== v) : [...f.outreach_channels, v] };
    });
  };

  const uploadCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) return setError("Choose a CSV or spreadsheet.");
    setError(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await axios.post(
        `${BACKEND}/api/reactivation/public/intake/${token}/upload`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCsvResult(data);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.business_name.trim()) return setError("Business name is required.");
    if (!intake.csv_uploaded) return setError("Upload your CRM CSV first.");
    if (!form.list_type) return setError("Pick what this list represents.");
    if (!form.list_age) return setError("Pick roughly how old this data is.");
    if (!form.ai_reference_context.trim()) return setError("Tell us what the AI should reference.");
    if (!form.avg_deal_value.trim()) return setError("Roughly what's a typical job/sale worth?");
    if (!form.consent_confirmed) return setError("Please confirm the consent statement.");
    if (form.outreach_channels.length === 0) return setError("Pick at least one outreach channel.");
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/reactivation/public/intake/${token}/submit`, form);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  const labelStyle = { ...eyebrow(), display: "block", marginBottom: 8 };
  const inputStyle = { width: "100%", padding: "12px 14px", background: T.cream, color: T.ink, border: `1px solid ${T.line}`, fontFamily: "inherit", fontSize: 15 };
  const sectionStyle = { padding: "20px 24px", background: "rgba(255,255,255,0.03)", border: `1px solid ${T.line}`, marginBottom: 16 };

  return (
    <PageShell testId="reactivation-intake-page">
      <TopNav activeKey="" />
      <section style={{ padding: "80px 0 40px" }}>
        <div style={container()}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div style={{ ...eyebrow(), color: T.coral }}>
              <Database size={12} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
              Database Reactivation · Onboarding
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(40px, 5vw, 64px)", lineHeight: 1.02, letterSpacing: "-0.03em", margin: "12px 0 20px", color: T.ink }}>
              {submitted ? <>You're all set — <em style={italicAccent(T.coral)}>we're taking it from here.</em></> : <>Give us <em style={italicAccent(T.coral)}>the fuel</em> to reactivate your leads.</>}
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.55, color: T.inkMuted, maxWidth: 620 }}>
              {submitted
                ? "Your CRM import + campaign context landed. Our team will build out your personalised sequence and kick off outreach within 48 hours."
                : "One quick form so we can personalise every message — not blast generic templates. Takes ~4 minutes."}
            </p>

            {submitted && (
              <div style={{ marginTop: 32, padding: 24, background: "rgba(126,231,135,0.08)", border: "1px solid rgba(126,231,135,0.35)" }} data-testid="intake-submitted-banner">
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#7EE787", fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  <CheckCircle2 size={18} /> Submitted
                </div>
                <p style={{ marginTop: 10, color: T.ink, fontSize: 15 }}>Thanks {intake.client_business_name || ""} — talk soon.</p>
              </div>
            )}

            {!submitted && (
              <form onSubmit={submit} style={{ marginTop: 40 }}>
                {/* 1. Business name */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>1. Business name *</label>
                  <input required data-testid="intake-business-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} style={inputStyle} />
                </div>

                {/* 2. CSV upload */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>2. CRM / lead list upload (CSV) *</label>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="file" accept=".csv,.tsv,.txt" onChange={(e) => setCsvFile(e.target.files?.[0])} disabled={intake.csv_uploaded} data-testid="intake-csv-file" style={{ ...inputStyle, padding: 8, flex: 1, minWidth: 240 }} />
                    <button type="button" onClick={uploadCsv} disabled={uploading || !csvFile || intake.csv_uploaded} data-testid="intake-csv-upload" style={{ ...btnCoral(), padding: "12px 20px", fontSize: 14, cursor: uploading ? "wait" : "pointer" }}>
                      <Upload size={15} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
                      {intake.csv_uploaded ? "Uploaded" : uploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                  {csvResult && (
                    <div data-testid="intake-csv-summary" style={{ marginTop: 12, padding: 12, background: "rgba(126,231,135,0.06)", border: "1px solid rgba(126,231,135,0.25)", fontSize: 13, color: T.ink }}>
                      <FileText size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
                      <strong>{csvResult.filename}</strong> · {csvResult.kept ?? csvResult.inserted ?? 0} kept
                      {csvResult.duplicates != null && <> · {csvResult.duplicates} dupes</>}
                      {csvResult.invalid_phone != null && <> · {csvResult.invalid_phone} invalid phones</>}
                      {csvResult.outside_window != null && <> · {csvResult.outside_window} outside 2yr window</>}
                    </div>
                  )}
                </div>

                {/* 3. List type */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>3. What does this list represent? *</label>
                  <select required value={form.list_type} onChange={(e) => setForm({ ...form, list_type: e.target.value })} data-testid="intake-list-type" style={inputStyle}>
                    <option value="">— Choose one —</option>
                    {LIST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>

                {/* 4. List age */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>4. Roughly how old is this data?</label>
                  <select value={form.list_age} onChange={(e) => setForm({ ...form, list_age: e.target.value })} data-testid="intake-list-age" style={inputStyle}>
                    <option value="">— Choose one —</option>
                    {LIST_AGES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>

                {/* 5. AI reference context */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>5. What should the AI reference when reaching out? *</label>
                  <p style={{ fontSize: 13, color: T.inkMuted, margin: "0 0 10px" }}>
                    What did these leads originally inquire about? Give us context so outreach sounds personal, not generic.
                  </p>
                  <textarea required rows={5} value={form.ai_reference_context} onChange={(e) => setForm({ ...form, ai_reference_context: e.target.value })} data-testid="intake-ai-context" style={{ ...inputStyle, resize: "vertical" }} placeholder="e.g. Most were homeowners asking about summer HVAC tune-ups or getting quotes for a new AC unit. A handful asked about ductless mini-splits. Reference their region (Austin area) and remind them of the seasonal maintenance we typically pitch."/>
                </div>

                {/* 6. Average deal value */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>6. Average deal value *</label>
                  <p style={{ fontSize: 13, color: T.inkMuted, margin: "0 0 10px" }}>
                    Roughly what's a typical job/sale worth? Helps us calibrate what a booked appointment is worth to you.
                  </p>
                  <input required data-testid="intake-deal-value" value={form.avg_deal_value} onChange={(e) => setForm({ ...form, avg_deal_value: e.target.value })} style={inputStyle} placeholder="$500 average · $200-$5,000 range" />
                </div>

                {/* 8. Preferred channel */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>7. Preferred outreach channel</label>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {CHANNELS.map((c) => {
                      const on = form.outreach_channels.includes(c.v);
                      return (
                        <button type="button" key={c.v} onClick={() => toggleChannel(c.v)} data-testid={`intake-channel-${c.v}`}
                          style={{ padding: "10px 18px", background: on ? T.coral : T.cream, color: on ? T.cream : T.ink, border: `1px solid ${on ? T.coral : T.line}`, fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer" }}>
                          {c.l}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 9. Booking destination */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>8. Booking destination</label>
                  <p style={{ fontSize: 13, color: T.inkMuted, margin: "0 0 10px" }}>
                    Where should booked appointments land? Calendar link, phone number, etc.
                  </p>
                  <input data-testid="intake-booking-destination" value={form.booking_destination} onChange={(e) => setForm({ ...form, booking_destination: e.target.value })} style={inputStyle} placeholder="https://calendly.com/... or +1 (555) 123-4567" />
                </div>

                {/* 7. Consent */}
                <div style={{ ...sectionStyle, background: "rgba(229,83,59,0.06)", borderColor: "rgba(229,83,59,0.3)" }}>
                  <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }} data-testid="intake-consent-label">
                    <input type="checkbox" checked={form.consent_confirmed} onChange={(e) => setForm({ ...form, consent_confirmed: e.target.checked })} data-testid="intake-consent-check" style={{ marginTop: 3, width: 18, height: 18, cursor: "pointer" }} />
                    <div>
                      <div style={{ ...eyebrow(), color: T.coral, marginBottom: 4 }}>9. Consent confirmation *</div>
                      <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>
                        I confirm this data is my own business's customer/lead data and these contacts have previously engaged with my business.
                      </div>
                    </div>
                  </label>
                </div>

                {error && (
                  <div style={{ padding: 12, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.35)", color: "#FCA5A5", fontSize: 14, marginBottom: 16 }} data-testid="intake-error">
                    {error}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                  <button type="submit" disabled={submitting} data-testid="intake-submit-btn" style={{ ...btnCoral(), padding: "16px 28px", fontSize: 15, cursor: submitting ? "wait" : "pointer" }}>
                    <Sparkles size={16} style={{ display: "inline", marginRight: 8, verticalAlign: "-3px" }} />
                    {submitting ? "Submitting…" : "Submit onboarding"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </PageShell>
  );
}
